<template>
  <div class="z-[40] relative min-h-screen animated-gradient pt-24">
    <div class="container mx-auto px-4 py-8 sm:px-6 lg:px-8 max-w-4xl">
      <!-- Loading State -->
      <div v-if="isSubmitting" class="text-center py-12">
        <p class="text-lg">{{ t('pages.join.submitting') }}</p>
      </div>

      <!-- Success State -->
      <div v-else-if="isSubmitted" class="text-center py-12 space-y-4">
        <h1 class=" text-dark-teal-600 text-center text-5xl font-medium md:text-6xl md:leading-[0.9] lg:text-8xl mb-12">{{ t('pages.join.success.title') }}</h1>
        <p class="text-lg text-dark-teal-600 ">{{ t('pages.join.success.message') }}</p>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="text-center py-12 space-y-4">
        <h1 class="text-4xl font-serif mb-4 text-red-600">{{ t('pages.join.error.title') }}</h1>
        <p class="text-lg">{{ error }}</p>
        <button
          @click="resetForm"
          class="px-6 py-2 bg-black text-white hover:bg-gray-800 transition-colors"
        >
          {{ t('pages.join.error.retry') }}
        </button>
      </div>

      <!-- Survey Form -->
      <div v-else>
        <div class="mb-8 space-y-8">
          <h1 class="text-dark-teal-600 text-center text-5xl font-medium md:text-6xl md:leading-[0.9] lg:text-8xl mb-12">{{ t('pages.join.title') }}</h1>
          
          <!-- Early Bird Section -->
          <div class="max-w-3xl mx-auto space-y-6">
            <div class="space-y-2">
              <h2 class="text-center text-2xl md:text-3xl font-medium text-dark-teal-600">{{ t('pages.join.earlyBird.title') }}</h2>
              
              <!-- Catchy Summary (Always Visible) -->
              <div class="text-center text-md md:text-lg font-medium text-dark-teal-600 pb-12">
                <span v-if="activeDiscount">{{ activeDiscount }}</span>
                <span v-else>{{ t('pages.join.earlyBird.summary') }}</span>
              </div>
              
              <!-- Expandable Content -->
              <div class="space-y-4">
                <!-- Read More/Less Button -->
                <button
                  @click="isEarlyBirdExpanded = !isEarlyBirdExpanded"
                  :aria-label="isEarlyBirdExpanded ? t('pages.join.earlyBird.readLess') : t('pages.join.earlyBird.readMore')"
                  :aria-expanded="isEarlyBirdExpanded"
                  class="text-lg underline hover:text-dark-teal-600 transition-colors mx-auto block mb-12"
                >
                  {{ isEarlyBirdExpanded ? t('pages.join.earlyBird.readLess') : t('pages.join.earlyBird.readMore') }}
                </button>
                
                <!-- Expandable Content -->
                <Transition name="fade">
                  <div
                    v-show="isEarlyBirdExpanded"
                    class="space-y-6"
                  >
                  <p class="text-lg">{{ t('pages.join.earlyBird.description') }}</p>
                  
                  <!-- Early Bird Tiers -->
                  <div class="space-y-4 border-t border-b border-dark-teal-600/20 py-6">
                    <div 
                      v-for="(tier, index) in earlyBirdTiers" 
                      :key="index"
                      :class="[
                        'flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 py-3 border-b border-dark-teal-600/10 last:border-b-0 rounded-lg p-4 transition-colors',
                        getTierStatus(index) === 'active' 
                          ? 'border-2 border-coral-600 bg-coral-50' 
                          : getTierStatus(index) === 'past'
                          ? 'border-2 border-gray-300 bg-gray-50 opacity-60'
                          : 'border-2 border-green-200 bg-white/50'
                      ]"
                    >
                      <div class="flex-1">
                        <div class="flex items-center gap-2 mb-1">
                          <div class="font-medium text-lg text-dark-teal-600">{{ tier.date }}</div>
                          <span 
                            :class="[
                              'text-xs font-medium px-2 py-1 rounded uppercase',
                              getTierStatus(index) === 'active' 
                                ? 'bg-coral-600 text-white' 
                                : getTierStatus(index) === 'past'
                                ? 'bg-gray-400 text-white'
                                : 'bg-green-200 text-dark-teal-600'
                            ]"
                          >
                            {{ getTierStatusLabel(index) }}
                          </span>
                        </div>
                        <div class="text-sm text-dark-teal-700 mt-1">{{ tier.description }}</div>
                      </div>
                      <div class="text-2xl md:text-3xl font-medium text-dark-teal-600 sm:text-right">
                        {{ tier.discount }}
                      </div>
                    </div>
                  </div>
                  
                  <p class="text-center">
                    <NuxtLink :to="localePath('pricing')" class="underline hover:text-dark-teal-600 transition-colors text-lg">
                      {{ t('pages.join.earlyBird.pricingLink') }}
                    </NuxtLink>
                  </p>
                  </div>
                </Transition>
              </div>
            </div>
          </div>
        </div>
        <ClientOnly>
          <template v-if="survey">
            <div class="min-h-[400px]">
              <SurveyComponent :model="survey" class="rounded-lg z-[200] shadow-lg transition-all duration-900"/>
            </div>
          </template>
          <template #fallback>
            <div class="text-center py-12 min-h-[400px] flex items-center justify-center">
              <div class="space-y-4">
                <div class="animate-pulse">
                  <div class="h-4 bg-dark-teal-200 rounded w-64 mx-auto mb-4"></div>
                  <div class="h-4 bg-dark-teal-200 rounded w-48 mx-auto"></div>
                </div>
                <p class="text-lg text-dark-teal-600">{{ t('pages.join.loading') || 'Loading form...' }}</p>
              </div>
            </div>
          </template>
        </ClientOnly>
        <div class="mt-8 max-w-2xl mx-auto">
          <p class="text-lg text-center">{{ t('pages.join.intro') }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, nextTick, watch } from 'vue'
import { Model } from 'survey-core'
import { SurveyComponent } from 'survey-vue3-ui'
import 'survey-core/defaultV2.min.css'
import { json } from '~/data/survey-json'
import { themeJson } from '~/data/survey-theme'
import { useI18n, useLocalePath } from '#imports'
import { useSeo } from '~/composables/useSeo'

const { t, tm } = useI18n()
const localePath = useLocalePath()

const earlyBirdTiers = computed(() => (tm('pages.join.earlyBird.tiers') as Array<{ date: string; discount: string; description: string }>) ?? [])
const isEarlyBirdExpanded = ref(false)

// Tier date thresholds (end dates for each discount period) - same as pricing page
const tierEndDates = [
  new Date('2025-11-15'), // Before Nov 15, 2025: 70%
  new Date('2025-12-15'), // Before Dec 15, 2025: 50%
  new Date('2026-01-15'), // Before Jan 15, 2026: 25%
  new Date('2026-02-15'), // Before Feb 15, 2026: 10%
  new Date('2026-03-15'), // From Mar 15, 2026: Full fees
]

// Helper function to compare dates ignoring time (only day/month/year)
const compareDatesOnly = (date1: Date, date2: Date): number => {
  const d1 = new Date(date1.getFullYear(), date1.getMonth(), date1.getDate())
  const d2 = new Date(date2.getFullYear(), date2.getMonth(), date2.getDate())
  if (d1 < d2) return -1
  if (d1 > d2) return 1
  return 0
}

// Determine tier status: 'past', 'active', or 'future'
const getTierStatus = (index: number): 'past' | 'active' | 'future' => {
  const now = new Date()
  const tierEndDate = tierEndDates[index]
  if (!tierEndDate) {
    return 'future' // Default to future if date is missing
  }
  const previousTierEndDate = index > 0 ? tierEndDates[index - 1] : null
  
  // For the first tier (70% discount), it's active from the start until Nov 15, 2025 (inclusive)
  if (index === 0) {
    if (compareDatesOnly(now, tierEndDate) <= 0) {
      return 'active'
    }
    return 'past'
  }
  
  // For the last tier (full fees), it's active from Mar 15, 2026 onwards (inclusive)
  if (index === tierEndDates.length - 1) {
    if (compareDatesOnly(now, tierEndDate) >= 0) {
      return 'active'
    }
    return 'future'
  }
  
  // For middle tiers, they're active between the day after previous tier's end and their own end date (inclusive)
  // Previous tier ends on the 15th, so next tier starts on the 16th
  if (previousTierEndDate) {
    const dayAfterPrevious = new Date(previousTierEndDate)
    dayAfterPrevious.setDate(dayAfterPrevious.getDate() + 1)
    
    if (compareDatesOnly(now, dayAfterPrevious) >= 0 && compareDatesOnly(now, tierEndDate) <= 0) {
      return 'active'
    }
  }
  
  // Past if we've passed this tier's end date
  if (compareDatesOnly(now, tierEndDate) > 0) {
    return 'past'
  }
  
  return 'future'
}

// Get localized status label
const getTierStatusLabel = (index: number): string => {
  const status = getTierStatus(index)
  if (status === 'active') {
    return t('pages.join.earlyBird.status.active')
  } else if (status === 'past') {
    return t('pages.join.earlyBird.status.past')
  } else {
    return t('pages.join.earlyBird.status.future')
  }
}

// Get the currently active discount for display
const activeDiscount = computed(() => {
  const activeIndex = earlyBirdTiers.value.findIndex((_, index) => getTierStatus(index) === 'active')
  if (activeIndex !== -1 && activeIndex < earlyBirdTiers.value.length) {
    const tier = earlyBirdTiers.value[activeIndex]
    if (tier?.discount) {
      return `${tier.discount} ${t('pages.join.earlyBird.status.active').toLowerCase()} ${t('pages.join.earlyBird.summary')}`
    }
  }
  return null
})

// localStorage key for tracking form submission
const STORAGE_KEY = 'join-form-submitted'

// Create survey model only on client side to avoid SSR hydration issues
const survey = ref<Model | null>(null)
const isSubmitting = ref(false)
const isSubmitted = ref(false)
const error = ref<string | null>(null)

// Check if form has already been submitted
const checkIfAlreadySubmitted = () => {
  if (typeof window === 'undefined' || !window.localStorage) return false
  return localStorage.getItem(STORAGE_KEY) === 'true'
}

// Mark form as submitted in localStorage
const markAsSubmitted = () => {
  if (typeof window !== 'undefined' && window.localStorage) {
    localStorage.setItem(STORAGE_KEY, 'true')
  }
}

// Simple scroll to top function - scrolls to the very top (0)
const scrollToTop = () => {
  if (typeof window === 'undefined') return
  
  // Use multiple methods to ensure scroll works
  window.scrollTo({ top: 0, behavior: 'smooth' })
  document.documentElement.scrollTo({ top: 0, behavior: 'smooth' })
  document.body.scrollTo({ top: 0, behavior: 'smooth' })
  
  // Also try instant scroll as fallback
  setTimeout(() => {
    window.scrollTo(0, 0)
    document.documentElement.scrollTop = 0
    document.body.scrollTop = 0
  }, 100)
}

// Watch for when thank you screen appears and scroll to top
watch(isSubmitted, (newValue) => {
  if (newValue) {
    nextTick(() => {
      setTimeout(() => {
        scrollToTop()
      }, 100)
    })
  }
})

onMounted(() => {
  // Check if form was already submitted
  if (checkIfAlreadySubmitted()) {
    isSubmitted.value = true
    return // Don't initialize the form if already submitted
  }
  
  // Lazy load SurveyJS to improve initial page load
  // Use requestIdleCallback or setTimeout to defer initialization
  const initSurvey = () => {
    // Initialize survey only on client side
    const surveyModel = new Model(json)
    surveyModel.applyTheme(themeJson)
    
    // Disable form if already submitted (prevent multiple submissions)
    if (checkIfAlreadySubmitted()) {
      surveyModel.mode = 'display' // Set to display mode to prevent interaction
      isSubmitted.value = true
      survey.value = surveyModel
      return
    }
    
    // Scroll to top when page changes (next/prev buttons)
    surveyModel.onCurrentPageChanged.add(() => {
      setTimeout(() => {
        scrollToTop()
      }, 100)
    })
    
    // Scroll to top when form is completed (submit button)
    surveyModel.onComplete.add(async (sender) => {
    // Prevent submission if already submitted
    if (checkIfAlreadySubmitted()) {
      isSubmitted.value = true
      return
    }
    
    scrollToTop()
    isSubmitting.value = true
    error.value = null

    try {
      const config = useRuntimeConfig()
      const makeWebhook = config.public.makeWebhook as { url?: string; apiKey?: string } | undefined

      if (!makeWebhook?.url || !makeWebhook?.apiKey) {
        throw new Error('Webhook configuration not found')
      }

      // Get reCAPTCHA token (invisible, client-side only)
      // Lazy load reCAPTCHA only when form is submitted to reduce initial bundle size
      let recaptchaToken: string | null = null
      if (import.meta.client) {
        try {
          // Initialize reCAPTCHA plugin if not already initialized
          const nuxtApp = useNuxtApp()
          if (nuxtApp.$initReCaptcha) {
            await nuxtApp.$initReCaptcha()
          }
          
          const { useReCaptcha } = await import('vue-recaptcha-v3')
          const recaptcha = useReCaptcha()
          if (recaptcha?.executeRecaptcha) {
            recaptchaToken = await recaptcha.executeRecaptcha('join_form_submit')
            console.log('✅ reCAPTCHA token generated:', recaptchaToken ? `${recaptchaToken.substring(0, 20)}...` : 'null')
          } else {
            console.warn('⚠️ reCAPTCHA not available - executeRecaptcha is undefined')
          }
        } catch (recaptchaError) {
          console.warn('❌ reCAPTCHA token generation failed:', recaptchaError)
          // Continue without token if reCAPTCHA fails (graceful degradation)
        }
      }

      // Call Make.com webhook directly from client (for SSG compatibility)
      const response = await $fetch<{ ok?: boolean; error?: string; recordId?: string }>(makeWebhook.url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-make-apikey': makeWebhook.apiKey
        },
        body: {
          ...sender.data,
          recaptchaToken
        }
      })

      // Mark as submitted in localStorage
      markAsSubmitted()
      isSubmitted.value = true
    } catch (err: any) {
      console.error('Form submission error:', err)
      
      // Handle HTTP errors
      if (err.statusCode || err.status) {
        const status = err.statusCode || err.status
        if (status === 401 || status === 403) {
          error.value = 'Webhook authentication failed. Please check the API key configuration.'
        } else if (status === 404) {
          error.value = 'Webhook endpoint not found. Please verify the webhook URL is correct.'
        } else if (status >= 500) {
          error.value = 'Webhook service error. Please try again later.'
        } else {
          error.value = err.response?.data?.message || err.message || t('pages.join.error.default')
        }
      } else {
        error.value = err.message || t('pages.join.error.default')
      }
    } finally {
      isSubmitting.value = false
    }
  })
  
  survey.value = surveyModel
  }
  
  // Defer SurveyJS initialization to improve Time to Interactive
  // Use requestIdleCallback if available, otherwise fallback to setTimeout
  if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
    requestIdleCallback(initSurvey, { timeout: 2000 })
  } else {
    setTimeout(initSurvey, 100)
  }
})

const resetForm = () => {
  scrollToTop()
  isSubmitted.value = false
  error.value = null
  // Note: We don't clear localStorage here to maintain the "submit once" restriction
  // If you want to allow resubmission, you would need to clear localStorage:
  // if (typeof window !== 'undefined' && window.localStorage) {
  //   localStorage.removeItem(STORAGE_KEY)
  // }
  if (survey.value) {
    survey.value.clear()
    survey.value.render()
  }
}

// SEO
useSeo({
  title: t('pages.join.meta.title'),
  description: t('pages.join.meta.description'),
  keywords: 'join, apply, application, ommi.shop, artisan, candidate'
})
</script>

<style scoped>
.join-page-container {
  padding-top: calc(var(--demo-alert-height, 0px) + 3rem); /* 48px header on mobile */
}

@media (min-width: 1024px) {
  .join-page-container {
    padding-top: calc(var(--demo-alert-height, 0px) + 4rem); /* 64px header on desktop */
  }
}

/* Hide SurveyJS header completely */
:deep(.sv-header),
:deep(.sv-header__content) {
  display: none !important;
}

/* Fix SurveyJS heading hierarchy - ensure proper h1-h6 order */
/* SurveyJS uses h5 for question titles, but we need proper hierarchy */
:deep(.sv_q_title) {
  /* SurveyJS question titles - ensure they use appropriate heading level */
  font-size: 1.25rem;
  font-weight: 600;
  line-height: 1.5;
}

/* Override any h5 elements in SurveyJS to use semantic structure */
:deep(.sv_q_title h5) {
  font-size: inherit;
  font-weight: inherit;
  margin: 0;
}

/* Ensure SurveyJS question titles have proper contrast */
:deep(.sv_q_title),
:deep(.sv_q_title *) {
  color: #003836; /* dark-teal-600 for better contrast */
}

/* Fade transition for early bird expandable content */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
